import { useState } from 'react';
import { Button, Stack, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { TableToolbar } from '../../components/TableToolbar';
import { StatusBadge } from '../../components/StatusBadge';
import { invoicesApi, type Invoice, type CreateInvoiceInput } from '../../api/finance.api';
import { omsApi } from '../../api/oms.api';
import { vendorsApi } from '../../api/vendors.api';
import { clientsApi } from '../../api/clients.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

export function InvoiceListPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<CreateInvoiceInput>({ omsId: 0, partyType: 'vendor', amount: 0, taxAmount: 0 });
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['invoices', page, search], queryFn: () => invoicesApi.list({ page, limit: 25, search: search || undefined }) });
  const omsOptions = useQuery({ queryKey: ['oms', 'forInv'], queryFn: () => omsApi.list({ limit: 100 }), enabled: open });
  const vendors = useQuery({ queryKey: ['vendors', 'forInv'], queryFn: () => vendorsApi.list({ limit: 100 }), enabled: open && form.partyType === 'vendor' });
  const clients = useQuery({ queryKey: ['clients', 'forInv'], queryFn: () => clientsApi.list({ limit: 100 }), enabled: open && form.partyType === 'client' });

  const createMut = useMutation({
    mutationFn: () => invoicesApi.create(form),
    onSuccess: () => { setOpen(false); setForm({ omsId: 0, partyType: 'vendor', amount: 0, taxAmount: 0 }); qc.invalidateQueries({ queryKey: ['invoices'] }); },
    onError: (e) => setError(extractApiError(e)),
  });

  const columns: Column<Invoice>[] = [
    { key: 'code', label: 'Invoice', width: 150 },
    { key: 'partyType', label: 'Party', render: (r) => `${r.partyType} #${r.vendorId ?? r.clientId}` },
    { key: 'invoiceNumber', label: 'Ext. No.' },
    { key: 'totalAmount', label: 'Total', align: 'right', render: (r) => `₹${r.totalAmount.toLocaleString()}` },
    { key: 'paidAmount', label: 'Paid', align: 'right', render: (r) => `₹${r.paidAmount.toLocaleString()}` },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <>
      <PageHeader title="Invoices" subtitle="Vendor payables and client receivables." action={hasPermission('invoice.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setError(null); setOpen(true); }}>New Invoice</Button>} />
      <TableToolbar search={search} onSearch={(v) => { setSearch(v); setPage(1); }} searchPlaceholder="Search invoice no." />
      <DataTable columns={columns} rows={data?.data ?? []} loading={isLoading} page={page} limit={25} total={data?.meta?.total ?? 0} onPageChange={setPage} onLimitChange={() => undefined} rowKey={(r) => r.id} emptyTitle="No invoices yet" />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New Invoice</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField select label="OMS" value={form.omsId || ''} onChange={(e) => setForm((f) => ({ ...f, omsId: Number(e.target.value) }))} fullWidth>
              {(omsOptions.data?.data ?? []).map((o) => <MenuItem key={o.id} value={o.id}>{o.omsNumber} — {o.title}</MenuItem>)}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField select label="Party Type" value={form.partyType} onChange={(e) => setForm((f) => ({ ...f, partyType: e.target.value as 'vendor' | 'client', vendorId: null, clientId: null }))} sx={{ minWidth: 140 }}>
                <MenuItem value="vendor">Vendor</MenuItem><MenuItem value="client">Client</MenuItem>
              </TextField>
              {form.partyType === 'vendor' ? (
                <TextField select label="Vendor" value={form.vendorId ?? ''} onChange={(e) => setForm((f) => ({ ...f, vendorId: Number(e.target.value) }))} fullWidth>
                  {(vendors.data?.data ?? []).map((v) => <MenuItem key={v.id} value={v.id}>{v.vendorName}</MenuItem>)}
                </TextField>
              ) : (
                <TextField select label="Client" value={form.clientId ?? ''} onChange={(e) => setForm((f) => ({ ...f, clientId: Number(e.target.value) }))} fullWidth>
                  {(clients.data?.data ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.clientName}</MenuItem>)}
                </TextField>
              )}
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Invoice No." value={form.invoiceNumber ?? ''} onChange={(e) => setForm((f) => ({ ...f, invoiceNumber: e.target.value }))} fullWidth />
              <TextField label="Invoice Date" type="date" InputLabelProps={{ shrink: true }} value={form.invoiceDate ?? ''} onChange={(e) => setForm((f) => ({ ...f, invoiceDate: e.target.value || undefined }))} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Amount" type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: Number(e.target.value) }))} fullWidth />
              <TextField label="Tax" type="number" value={form.taxAmount} onChange={(e) => setForm((f) => ({ ...f, taxAmount: Number(e.target.value) }))} fullWidth />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!form.omsId || (form.partyType === 'vendor' ? !form.vendorId : !form.clientId) || !form.amount} onClick={() => createMut.mutate()}>Create</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
